import {
  BellOutlined,
  ExperimentOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { history, useLocation } from '@umijs/max';
import {
  Alert,
  App,
  Badge,
  Button,
  Descriptions,
  Drawer,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import React from 'react';
import { useBusiness } from '@/prototype/context';
import {
  campuses,
  type Role,
  roles,
  teacherName,
  teachers,
} from '@/prototype/types';
export function PrototypeHeader() {
  const { service, state } = useBusiness();
  return (
    <Space size={0}>
      <Button
        type="text"
        aria-label="业务消息"
        onClick={() => history.push('/message')}
        icon={
          <Badge
            count={
              state
                ? service?.list('message').filter((e) => e.status === 'unread')
                    .length
                : 0
            }
            size="small"
          >
            <BellOutlined />
          </Badge>
        }
      />
    </Space>
  );
}
export default function Controls({
  compact = false,
  fixedCampus,
}: {
  compact?: boolean;
  fixedCampus?: string;
}) {
  const { service, state } = useBusiness();
  const location = useLocation();
  const { message, modal } = App.useApp();
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    if (new URLSearchParams(location.search).get('demo') === '1') setOpen(true);
  }, [location.search]);
  if (!service || !state) return null;
  const update = async (patch: Partial<typeof state.context>) => {
    try {
      await service.context({ ...state.context, ...patch });
    } catch (e) {
      message.error(String(e));
    }
  };
  return (
    <>
      <div className={compact ? 'srbs-context-compact' : 'srbs-context'}>
        {fixedCampus ? (
          <Tag>{fixedCampus}</Tag>
        ) : (
          <Select
            aria-label="业务数据范围"
            value={state.context.campus}
            style={{ width: 145 }}
            options={[...campuses, '全部校区'].map((value) => ({
              value,
              label: value === '全部校区' ? '全部授权校区' : teacherName(value),
            }))}
            onChange={(campus) => void update({ campus })}
          />
        )}
        <Tag title={`模拟业务日期 ${state.now.slice(0, 10)}`}>模拟数据</Tag>
        <Button
          type="text"
          aria-label="业务视角与演示设置"
          icon={<MoreOutlined />}
          onClick={() => setOpen(true)}
        />
      </div>
      <Drawer
        title="业务视角与演示设置"
        open={open}
        onClose={() => {
          setOpen(false);
          if (location.search.includes('demo'))
            history.replace(location.pathname);
        }}
        size="large"
      >
        <Space orientation="vertical" style={{ width: '100%' }} size="large">
          <Alert
            type="info"
            showIcon
            title="业务视角不会更改真实登录身份"
            description="同一真实账号与租户下共享演示对象。设备、分析及消息均为本地模拟，不调用硬件和模型。"
          />
          <Descriptions
            column={1}
            items={[
              {
                key: 'role',
                label: '当前业务视角',
                children: roles[state.context.role],
              },
              {
                key: 'tenant',
                label: '真实租户编号',
                children: service.identity.tenantUuid,
              },
              { key: 'version', label: '数据版本', children: state.revision },
            ]}
          />
          <Select
            aria-label="业务角色视角"
            value={state.context.role}
            style={{ width: '100%' }}
            options={Object.entries(roles).map(([value, label]) => ({
              value,
              label,
            }))}
            onChange={(role: Role) => void update({ role })}
          />
          <Select
            aria-label="演示人员"
            value={state.context.viewMemberUuid}
            style={{ width: '100%' }}
            options={teachers.map((value) => ({
              label: teacherName(value),
              value,
            }))}
            onChange={(viewMemberUuid) => void update({ viewMemberUuid })}
          />
          <Button
            onClick={() =>
              void service.tick(true).catch((e) => message.error(String(e)))
            }
          >
            推进模拟任务一步
          </Button>
          <Button
            onClick={() => {
              const blob = new Blob([JSON.stringify(state, null, 2)], {
                type: 'application/json',
              });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = '智能录音工牌系统-演示数据.json';
              a.click();
              setTimeout(() => URL.revokeObjectURL(url), 1000);
            }}
          >
            导出演示数据
          </Button>
          <Button
            danger
            onClick={() =>
              modal.confirm({
                title: '重置当前演示数据？',
                content:
                  '将清除当前真实账号及租户分区中的模拟操作、草稿和历史。真实会话与主题偏好不受影响。',
                okText: '确认重置',
                cancelText: '取消',
                onOk: () => service.reset(),
              })
            }
          >
            重置演示数据
          </Button>
        </Space>
      </Drawer>
    </>
  );
}
